import React, { useEffect, useState } from 'react';
import { ShortPortal } from "../typings";
import { Align, Row } from "../components/Flex";
import { createNewPortal, fetchPortals } from "./drills-api";
import PortalRow from "./PortalRow";
import TextField from "../components/TextField";
import { FaAngleLeft, FaAngleRight, FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import { toast } from "react-toastify";
import { useApi } from "../api-context";
import CreatePortalDialog from "./CreatePortalDialog";
import { useNavigate } from "react-router-dom";

function PortalsPage() {
    const navigate = useNavigate();
    const api = useApi();
    const [total, setTotal] = useState(0);
    const [portals, setPortals] = useState<ShortPortal[]>([]);

    const [page, setPage] = useState(0);
    const [nameFilter, setNameFilter] = useState('');
    const [orderBy, setOrderBy] = useState('id');
    const [orderDirection, setOrderDirection] = useState('ASC');
    const [dialogOpen, setDialogOpen] = useState(false);

    const loadDrills = (page: number, orderBy: string, orderDirection: string, nameFilter?: string) => {
        fetchPortals(api, { orderBy, orderDirection, page, nameFilter })
            .then(res => {
                setTotal(res.total);
                setPortals(res.portals);
            })
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load portals');
                }
            });
    };

    const onFilter = (nameFilter: string) => {
        setPage(0);
        setNameFilter(nameFilter);
    }

    const onChangeOrder = (newOrderBy: string) => {
        setPage(0);
        if (newOrderBy === orderBy) {
            setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
            return;
        }
        setOrderBy(newOrderBy);
        setOrderDirection('ASC')
    }

    useEffect(() => {
        loadDrills(page, orderBy, orderDirection, nameFilter || undefined);
    }, [orderBy, orderDirection, nameFilter, page]);

    const onCreatePortal = (name: string) => {
        createNewPortal(api, name)
            .then((id) => {
                navigate(`/portal/${id}`)
                toast.success('Successfully created portal');
            })
            .catch(() => toast.error('Failed to create portal'))
            .finally(() => {
                setDialogOpen(false);
            })
    };

    const onOpenDialog = () => setDialogOpen(true);
    const onCloseDialog = () => setDialogOpen(false);

    const renderColumnHeader = (title: string, prop: string, flex: number) => {
        return (
            <div style={{ flex, display: 'flex' }}>
                <div className="column-header" onClick={() => onChangeOrder(prop)}>
                    <div>{title}</div>
                    {
                        orderBy !== prop
                            ? <FaSort/>
                            : orderDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />
                    }
                </div>
            </div>
        );
    };

    const renderSearchHeader = () => {
        return (
            <Row align={Align.SPACE_BETWEEN} className="search-header">
                <TextField label="" value={nameFilter} setValue={onFilter} placeholder="Search by name"/>
                <div style={{ color: '#545452' }}>
                    {`Showing ${page * 10 + (total ? 1 : 0)}-${Math.min(total, (page + 1) * 10)} of ${total}`}
                </div>
            </Row>
        );
    };

    return (
        <div className="page-container drill-files-page">
            <Row gap={8} style={{marginBottom: '32px'}}>
                <div style={{ flex: 1 }} className="main-title">
                    Portals
                </div>
                <button
                    style={{ flex: 0, minWidth: '150px' }}
                    className="my-button"
                    onClick={onOpenDialog}
                >
                    CREATE
                </button>
            </Row>
            {renderSearchHeader()}
            <Row style={{ padding: '12px 16px', marginBottom: '12px' }}>
                {renderColumnHeader('Id', 'id', 2)}
                {renderColumnHeader('Name', 'name', 2)}
                {renderColumnHeader('Type', 'organisation_type', 2)}
                {renderColumnHeader('Federation', 'fname', 2)}
                {renderColumnHeader('Expires at', 'expiresAt', 1)}
                {renderColumnHeader('Created at', 'createdAt', 1)}
                <div style={{ width: '28px' }}/>
            </Row>
            <div className="content-container" style={{ marginBottom: '32px', gap: '6px' }}>
                {portals.map((portal) => (
                    <PortalRow
                        key={portal.id}
                        api={api}
                        portal={portal}
                    />
                ))}
            </div>
            <Row align={Align.END} gap={4}>
                <FaAngleLeft
                    className="pager-icon"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    size={20}
                    color="#545452"
                />
                <div style={{
                    fontSize: '16px',
                    fontFamily: 'InterRegular',
                    color: '#545452'
                }}>
                    {page + 1} of {Math.ceil(total / 10)}
                </div>
                <FaAngleRight
                    className="pager-icon"
                    size={20}
                    onClick={() => setPage(Math.min(Math.floor(total / 10), page + 1))}
                    color="#545452"
                />
            </Row>
            <CreatePortalDialog
                open={dialogOpen}
                onClose={onCloseDialog}
                onCreate={onCreatePortal}
            />
        </div>
    );
}

export default PortalsPage;
